import ClearIcon from '@mui/icons-material/Clear';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Chip,
  Divider,
  Drawer,
  IconButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import React from 'react';
import { useLocation } from 'react-router-dom';

import {
  BoardContract,
  BoardFilters,
  BoardSeverity,
  BoardState,
} from '../api/ach/board';
import {
  contractChatContext,
  contractChatDraft,
  issueChatContext,
  issueChatDraft,
} from '../components/Alerts/askAi';
import ContractBand from '../components/Alerts/ContractBand';
import IssueInspector from '../components/Alerts/IssueInspector';
import { flattenIssues, IssueRef } from '../components/Alerts/issueRef';
import PeopleFilter, { peopleInBoard, PersonOption } from '../components/Alerts/people';
import ruleLabel from '../components/Alerts/ruleLabels';
import { SEVERITY_META, SEVERITY_ORDER, SeverityDot } from '../components/Alerts/severity';
import {
  AckSource,
  captureAlertAcked,
  captureAlertInspected,
  captureAlertsViewed,
  captureAskAiContract,
  captureAskAiIssue,
} from '../components/Alerts/tracking';
import { SUB_NAVIGATION_HEIGHT, TOP_NAVIGATION_HEIGHT } from '../constants/style';
import { agentChatEnabled } from '../features/agentChat/chatEnabled';
import { openAgentChat } from '../features/agentChat/launcher';
import { useAuth } from '../hooks/useAuth';
import { useAcknowledgeGroup, useAcknowledgeIssue, useBoard } from '../hooks/useBoard';
import useDebounce from '../hooks/useDebounce';

const PER_PAGE = 25;

// Width of the inspector pane when it sits beside the ledger (lg and up).
const INSPECTOR_GRID = 'minmax(0, 8fr) minmax(380px, 5fr)';

const TABS: { state: BoardState; label: string }[] = [
  { state: 'open', label: 'Needs attention' },
  { state: 'acknowledged', label: 'In progress' },
  { state: 'resolved', label: 'Resolved' },
];

// Scope descriptor: My alerts (the logged-in user, in any role), All alerts, a picked
// set of people, or the Unassigned bucket (nobody in ANY role). people_id and
// people_unassigned are never both sent (the API 422s).
type Scope =
  | { kind: 'mine' }
  | { kind: 'all' }
  | { kind: 'people'; people: PersonOption[] }
  | { kind: 'unassigned' };

// Module-level so it stays referentially stable across renders.
const ALL_SCOPE: Scope = { kind: 'all' };
const NO_PEOPLE: PersonOption[] = [];

function scopeFilters(scope: Scope, myId?: number): Partial<BoardFilters> {
  switch (scope.kind) {
    case 'mine':
      return myId ? { people_id: [myId] } : {};
    case 'people':
      return { people_id: scope.people.map((p) => p.id) };
    case 'unassigned':
      return { people_unassigned: true };
    default:
      return {};
  }
}

const byName = (a: PersonOption, b: PersonOption) => a.name.localeCompare(b.name);

// idFilter turns a raw numeric-input string into a filter value: a positive integer,
// or undefined (no filter) for empty / non-numeric / non-positive input.
function idFilter(raw: string): number | undefined {
  const n = Number(raw);
  return raw.trim() !== '' && Number.isInteger(n) && n > 0 ? n : undefined;
}

// SeverityPill is a single-select severity facet: dot, label, and the SERVER-faceted
// count (never recomputed from the rows on the page).
function SeverityPill({
  label,
  count,
  severity,
  selected,
  onClick,
}: {
  label: string;
  count: number;
  severity?: BoardSeverity;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      data-testid={`severity-card-${label.toLowerCase()}`}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        borderRadius: 99,
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: (t) => (selected ? alpha(t.palette.primary.main, 0.08) : t.palette.background.paper),
        color: selected ? 'text.primary' : 'text.secondary',
        fontSize: 12.5,
        fontWeight: 550,
        opacity: count === 0 && !selected ? 0.5 : 1,
      }}
    >
      {severity && <SeverityDot severity={severity} size={8} />}
      {label}
      <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
        {count}
      </Box>
    </ButtonBase>
  );
}

function AlertsPage() {
  const { user } = useAuth();
  // My alerts is people_id=<my id>: the server matches me as trafficker OR account manager.
  const myId = user?.id;
  const theme = useTheme();
  const { pathname } = useLocation();
  const wide = useMediaQuery(theme.breakpoints.up('lg'));

  // The app's sub-nav is itself sticky (Layout/SubNav): everything sticky on this page
  // has to start below it, using the same federated offset rule.
  // eslint-disable-next-line @typescript-eslint/dot-notation
  const rawNavHeight = Number(theme?.mixins?.toolbar?.['@media (min-width:600px)']?.['minHeight']);
  const mainNavHeight = Number.isFinite(rawNavHeight) ? rawNavHeight : TOP_NAVIGATION_HEIGHT;
  const stickyTop = (import.meta.env.VITE_IS_FEDERATED ? mainNavHeight : 0) + SUB_NAVIGATION_HEIGHT;

  const [tab, setTab] = React.useState<BoardState>('open');
  const [severity, setSeverity] = React.useState<BoardSeverity | undefined>(undefined);
  const [scope, setScope] = React.useState<Scope>({ kind: 'mine' });
  const [ruleIds, setRuleIds] = React.useState<string[]>([]);
  const [campaignInput, setCampaignInput] = React.useState('');
  const [ioInput, setIoInput] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [selectedId, setSelectedId] = React.useState<number | null>(null);

  // Numeric id filters are debounced so each keystroke doesn't fire a board fetch.
  const campaignId = idFilter(useDebounce(campaignInput, 400));
  const insertionOrderId = idFilter(useDebounce(ioInput, 400));

  const filters: BoardFilters = React.useMemo(
    () => ({
      state: tab,
      severity,
      page,
      per_page: PER_PAGE,
      ...(ruleIds.length ? { rule_id: ruleIds } : {}),
      ...(campaignId ? { campaign_id: campaignId } : {}),
      ...(insertionOrderId ? { insertion_order_id: insertionOrderId } : {}),
      ...scopeFilters(scope, myId),
    }),
    [tab, severity, page, scope, myId, ruleIds, campaignId, insertionOrderId]
  );

  // A resolved debounced id filter is a new query — reset to the first page.
  React.useEffect(() => {
    setPage(1);
  }, [campaignId, insertionOrderId]);

  const { data, isLoading, isError, error, isFetching } = useBoard(filters);
  const ackIssue = useAcknowledgeIssue();
  const ackGroup = useAcknowledgeGroup();
  const acking = ackIssue.isLoading || ackGroup.isLoading;
  const readOnly = tab === 'resolved';

  // Every user-driven filter change restarts at page 1. Scope NEVER changes on its own:
  // the toggle is the single source of scope truth, and the my-scope zero state offers
  // an explicit button rather than substituting All behind the user's back.
  const refilter = React.useCallback(() => setPage(1), []);

  const counts = data?.counts;

  // Signature of the last board view reported to PostHog, so the 5-minute poll and
  // refocus refetches returning the same board do not double-count a view.
  const lastViewed = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!counts) {
      return;
    }
    const signature = JSON.stringify([filters, counts.by_state, counts.by_severity]);
    if (lastViewed.current === signature) {
      return;
    }
    lastViewed.current = signature;
    captureAlertsViewed(
      { tab, severity, scope: scope.kind, ruleIds, campaignId, insertionOrderId, page },
      counts
    );
  }, [counts, filters, tab, severity, scope, ruleIds, campaignId, insertionOrderId, page]);

  const totalForSeverity = counts
    ? SEVERITY_ORDER.reduce((sum, s) => sum + counts.by_severity[s], 0)
    : 0;

  // The roster is the server's by_person facet, which applies every filter EXCEPT the
  // people filter — that is what stops the menu collapsing to the one person picked.
  // It falls back to scraping the returned rows on deployments predating the facet, and
  // either way unions whoever is selected so a narrowed board cannot strand the filter.
  const byPerson = counts?.by_person;
  const peopleOptions = React.useMemo(() => {
    const roster: PersonOption[] = byPerson ?? peopleInBoard(data);
    const ids = new Set(roster.map((p) => p.id));
    const picked = scope.kind === 'people' ? scope.people : [];
    return [...roster, ...picked.filter((p) => !ids.has(p.id))].sort(byName);
  }, [byPerson, data, scope]);
  const byRule = counts?.by_rule;
  // Options are the facet keys UNION whatever is currently selected: a selected rule
  // that the server no longer facets must stay visible and un-checkable, or the filter
  // becomes invisible and unclearable.
  const ruleOptions = React.useMemo(
    () => [...new Set([...Object.keys(byRule ?? {}), ...ruleIds])].sort(),
    [byRule, ruleIds]
  );

  const askAiEnabled = agentChatEnabled({ envFlag: import.meta.env.VITE_AGENT_CHAT_ENABLED, pathname });

  // Render-order issue list: backs the inspector's selection and j/k navigation.
  const rows = React.useMemo(() => flattenIssues(data?.contracts ?? []), [data]);
  const selected = rows.find((r) => r.issue.id === selectedId) ?? null;

  const askAiIssue = React.useCallback((item: IssueRef) => {
    captureAskAiIssue(item);
    openAgentChat({ context: issueChatContext(item), draft: issueChatDraft(item) });
  }, []);

  const askAiContract = React.useCallback((contract: BoardContract) => {
    captureAskAiContract(contract.contract_id);
    openAgentChat({
      context: contractChatContext(contract.contract_id),
      draft: contractChatDraft(contract.contract_name, contract.contract_id, contract.severity_counts),
    });
  }, []);

  // react-query keeps `mutate` referentially stable, so the keyboard listener below
  // does not resubscribe on every render.
  const ackIssueById = ackIssue.mutate;

  // One tracked ack path per affordance. Capture-then-mutate: the mutation is
  // optimistic and fire-and-forget, so the click is the ack for analytics purposes.
  // `rows` carries every issue on the page, so the id -> IssueRef lookup never misses.
  const ackTracked = React.useCallback(
    (source: AckSource) => (issueId: number) => {
      const item = rows.find((r) => r.issue.id === issueId);
      if (item) {
        captureAlertAcked(item, source);
      }
      ackIssueById(issueId);
    },
    [rows, ackIssueById]
  );
  const ackFromRow = React.useMemo(() => ackTracked('row'), [ackTracked]);
  const ackFromInspector = React.useMemo(() => ackTracked('inspector'), [ackTracked]);

  // Keyboard triage: Esc dismisses the inspector, j/k walk the queue, a acks, i asks AI.
  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (target && (/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName) || target.isContentEditable)) {
        return;
      }
      // The agent chat drawer is non-modal and owns its own Esc: a key pressed inside it
      // must not also dismiss the inspector behind it.
      if (target?.closest?.('[data-ach-chat]')) {
        return;
      }
      // MUI menus/popovers bubble their typeahead keydowns to window: typing "a" to
      // jump to a person in an open dropdown must not ack the selected issue.
      if (target?.closest?.('[role="menu"],[role="listbox"],[role="dialog"]')) {
        return;
      }
      if (event.key === 'Escape') {
        setSelectedId(null);
        return;
      }
      if (event.key === 'j' || event.key === 'k') {
        if (!rows.length) {
          return;
        }
        const index = rows.findIndex((r) => r.issue.id === selectedId);
        const next = event.key === 'j' ? Math.min(index + 1, rows.length - 1) : Math.max(index - 1, 0);
        if (next !== index) {
          captureAlertInspected(rows[next], 'keyboard');
        }
        setSelectedId(rows[next].issue.id);
        return;
      }
      if (!selected) {
        return;
      }
      if (event.key === 'a' && !readOnly && selected.issue.state === 'open') {
        captureAlertAcked(selected, 'keyboard');
        ackIssueById(selected.issue.id);
      }
      if (event.key === 'i' && askAiEnabled) {
        askAiIssue(selected);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [rows, selectedId, selected, readOnly, askAiEnabled, askAiIssue, ackIssueById]);

  // Selection is keyed by PERSON ID, never by (person, role): a dual-role person is
  // rendered under both headings but toggles as one, so the count stays a distinct-people
  // count and the wire never carries a duplicate id.
  const togglePerson = React.useCallback(
    (person: PersonOption) => {
      refilter();
      setScope((prev) => {
        const current = prev.kind === 'people' ? prev.people : [];
        const next = current.some((p) => p.id === person.id)
          ? current.filter((p) => p.id !== person.id)
          : [...current, person];
        return next.length ? { kind: 'people', people: next } : ALL_SCOPE;
      });
    },
    [refilter]
  );

  // Unassigned is not a person and cannot be ORed with one on the wire, so the two
  // buckets swap rather than combine: whichever the user just clicked wins.
  const toggleUnassigned = React.useCallback(() => {
    refilter();
    setScope((prev) => (prev.kind === 'unassigned' ? ALL_SCOPE : { kind: 'unassigned' }));
  }, [refilter]);

  const clearPeople = React.useCallback(() => {
    refilter();
    setScope(ALL_SCOPE);
  }, [refilter]);

  const clearAllFilters = () => {
    refilter();
    setSeverity(undefined);
    setRuleIds([]);
    setCampaignInput('');
    setIoInput('');
    setScope(ALL_SCOPE);
  };

  // Every filter narrowing the board right now, as a removable chip. The zero-state
  // names them so an empty result can always be traced to the control that caused it.
  const activeFilters: { key: string; label: string; clear: () => void }[] = [];
  if (severity) {
    activeFilters.push({
      key: 'severity',
      label: `Severity: ${SEVERITY_META[severity].label}`,
      clear: () => {
        refilter();
        setSeverity(undefined);
      },
    });
  }
  ruleIds.forEach((id) =>
    activeFilters.push({
      key: `rule-${id}`,
      label: `Issue type: ${ruleLabel(id)}`,
      clear: () => {
        refilter();
        setRuleIds((prev) => prev.filter((r) => r !== id));
      },
    })
  );
  if (scope.kind !== 'all') {
    const scopeLabel =
      // eslint-disable-next-line no-nested-ternary
      scope.kind === 'mine'
        ? 'My alerts'
        : scope.kind === 'unassigned'
          ? 'Unassigned'
          : `People: ${scope.people.map((p) => p.name).join(', ')}`;
    activeFilters.push({
      key: 'scope',
      label: scopeLabel,
      clear: () => {
        refilter();
        setScope(ALL_SCOPE);
      },
    });
  }
  if (campaignInput.trim()) {
    activeFilters.push({
      key: 'campaign',
      label: `Campaign ID ${campaignInput.trim()}`,
      clear: () => {
        refilter();
        setCampaignInput('');
      },
    });
  }
  if (ioInput.trim()) {
    activeFilters.push({
      key: 'io',
      label: `IO ID ${ioInput.trim()}`,
      clear: () => {
        refilter();
        setIoInput('');
      },
    });
  }

  // Scope is a chip too, but it is not what "these filters" means in the zero state —
  // My-scope emptiness has its own message and its own explicit escape hatch.
  const narrowingFilters = activeFilters.filter((f) => f.key !== 'scope').length;

  const totalPages = data ? Math.max(1, Math.ceil(data.pagination.total_contracts / data.pagination.per_page)) : 1;

  const ledger = !isError && data && data.contracts.length > 0 && (
    // overflow:clip, never hidden: `hidden` makes the Paper a scroll container, which
    // re-anchors the bands' sticky headers to it — the first one then sits `stickyTop`
    // px below the Paper's top edge instead of flush, and none of them track the page.
    <Paper
      variant="outlined"
      // Dimmed and inert while a refetch is in flight, so a pill or tab click visibly
      // does something instead of leaving the previous rows looking live.
      sx={{
        borderRadius: 1.5,
        overflow: 'clip',
        transition: 'opacity 120ms ease',
        ...(isFetching ? { opacity: 0.55, pointerEvents: 'none' } : {}),
      }}
      data-testid="alerts-ledger"
    >
      {data.contracts.map((contract, index) => (
        <ContractBand
          key={contract.contract_id}
          contract={contract}
          first={index === 0}
          stickyTop={stickyTop}
          selectedIssueId={selectedId}
          onSelectIssue={(item) => {
            if (item.issue.id !== selectedId) {
              captureAlertInspected(item, 'row');
            }
            setSelectedId(item.issue.id);
          }}
          onAckIssue={ackFromRow}
          onAckGroup={(ruleId, contractId) => {
            // The bulk chip covers every open issue sharing the rule on this contract
            // (the contract's issues are all on this page — pagination is by contract):
            // report each one, so alert_acked stays a per-issue count.
            rows
              .filter(
                (r) =>
                  r.contract.contract_id === contractId &&
                  r.issue.rule_id === ruleId &&
                  r.issue.state === 'open'
              )
              .forEach((r) => captureAlertAcked(r, 'group'));
            ackGroup.mutate({ rule_id: ruleId, contract_id: contractId });
          }}
          onAskAiIssue={askAiIssue}
          onAskAiContract={askAiContract}
          askAiEnabled={askAiEnabled}
          readOnly={readOnly}
          acking={acking}
        />
      ))}
    </Paper>
  );

  const pagination = data && data.pagination.total_contracts > data.pagination.per_page && (
    <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" mt={2}>
      <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
        Previous
      </Button>
      <Typography variant="body2">
        Page {data.pagination.page} of {totalPages}
      </Typography>
      <Button size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
        Next
      </Button>
    </Stack>
  );

  const inspector = selected && (
    <IssueInspector
      item={selected}
      onClose={() => setSelectedId(null)}
      onAck={ackFromInspector}
      onAskAi={askAiIssue}
      askAiEnabled={askAiEnabled}
      acking={acking}
      readOnly={readOnly}
    />
  );

  const unackedCount = counts?.unacked_high_sev_over_24h ?? 0;

  // One banner, two shapes: a compact pill in the dead space right of the tabs when the
  // viewport is wide enough for it, otherwise the full-width row underneath them.
  const unackedBanner = (inline: boolean) =>
    unackedCount > 0 && (
      <Alert
        severity="warning"
        data-testid="unacked-banner"
        sx={
          inline
            ? {
                ml: 'auto',
                minWidth: 0,
                maxWidth: 440,
                py: 0,
                borderRadius: 2,
                alignItems: 'center',
                '& .MuiAlert-message': {
                  minWidth: 0,
                  py: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
                '& .MuiAlert-action': { py: 0, pl: 1, mr: -0.5 },
              }
            : { mb: 1.5, py: 0.25 }
        }
        action={
          <Button
            size="small"
            color="inherit"
            data-testid="unacked-banner-show"
            // The count is faceted for the view currently on screen, so this narrows
            // that view without touching the scope.
            onClick={() => {
              setPage(1);
              setTab('open');
              setSeverity('blocker');
            }}
          >
            Show me
          </Button>
        }
      >
        {inline
          ? `${unackedCount} high-severity unacked >24h`
          : `${unackedCount} high-severity alert(s) have been unacknowledged for over 24 hours.`}
      </Alert>
    );

  return (
    // The chat FAB floats bottom-right; the ledger gets that much scroll headroom so its
    // last row's Ack is never underneath it.
    <Box p={2} pb={askAiEnabled ? 18 : 2} data-testid="alerts-page">
      <Stack direction="row" alignItems="center" spacing={2.5} flexWrap="wrap" useFlexGap mb={1.5}>
        <Typography variant="h5" fontWeight={700}>
          Alerts
        </Typography>
        <Tabs
          value={tab}
          onChange={(_e, v) => {
            setTab(v);
            setPage(1);
          }}
          sx={{
            minHeight: 36,
            bgcolor: 'action.hover',
            borderRadius: 2,
            p: 0.375,
            '& .MuiTabs-indicator': { display: 'none' },
          }}
        >
          {TABS.map((t) => (
            <Tab
              key={t.state}
              value={t.state}
              data-testid={`state-tab-${t.state}`}
              sx={{
                minHeight: 30,
                py: 0.25,
                textTransform: 'none',
                fontWeight: 550,
                borderRadius: 1.5,
                '&.Mui-selected': { bgcolor: 'background.paper', color: 'text.primary' },
              }}
              label={
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <span>{t.label}</span>
                  {counts && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: 11,
                        px: 0.75,
                        borderRadius: 99,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      {counts.by_state[t.state]}
                    </Box>
                  )}
                </Stack>
              }
            />
          ))}
        </Tabs>

        {wide && unackedBanner(true)}
      </Stack>

      {!wide && unackedBanner(false)}

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        mb={1.5}
        data-testid="severity-cards"
      >
        <SeverityPill
          label="All"
          count={totalForSeverity}
          selected={severity === undefined}
          onClick={() => {
            setSeverity(undefined);
            refilter();
          }}
        />
        {SEVERITY_ORDER.map((sev) => (
          <SeverityPill
            key={sev}
            label={SEVERITY_META[sev].label}
            count={counts ? counts.by_severity[sev] : 0}
            severity={sev}
            selected={severity === sev}
            onClick={() => {
              setSeverity(sev);
              refilter();
            }}
          />
        ))}

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <ToggleButtonGroup
          exclusive
          size="small"
          value={scope.kind === 'mine' ? 'mine' : 'all'}
          onChange={(_e, v) => {
            if (!v) {
              return;
            }
            refilter();
            setScope(v === 'mine' ? { kind: 'mine' } : ALL_SCOPE);
          }}
        >
          <ToggleButton value="mine" sx={{ textTransform: 'none', py: 0.375 }}>
            My alerts
          </ToggleButton>
          <ToggleButton value="all" sx={{ textTransform: 'none', py: 0.375 }}>
            All alerts
          </ToggleButton>
        </ToggleButtonGroup>

        <PeopleFilter
          options={peopleOptions}
          selected={scope.kind === 'people' ? scope.people : NO_PEOPLE}
          unassigned={scope.kind === 'unassigned'}
          disabled={scope.kind === 'mine'}
          onTogglePerson={togglePerson}
          onToggleUnassigned={toggleUnassigned}
          onClear={clearPeople}
        />

        {ruleOptions.length > 0 && (
          <Select
            multiple
            size="small"
            displayEmpty
            value={ruleIds}
            onChange={(e) => {
              const v = e.target.value;
              setRuleIds(typeof v === 'string' ? v.split(',') : v);
              refilter();
            }}
            renderValue={(selectedRules) =>
              selectedRules.length === 0 ? 'Issue type' : `Issue type (${selectedRules.length})`
            }
            data-testid="issue-type-select"
            sx={{ minWidth: 160 }}
          >
            {ruleOptions.map((ruleId) => (
              <MenuItem key={ruleId} value={ruleId} data-testid={`issue-type-option-${ruleId}`}>
                <Checkbox size="small" checked={ruleIds.includes(ruleId)} />
                <ListItemText primary={ruleLabel(ruleId)} secondary={ruleId} />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {byRule?.[ruleId] ?? 0}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        )}

        <TextField
          size="small"
          type="number"
          label="Campaign ID"
          value={campaignInput}
          onChange={(e) => {
            setCampaignInput(e.target.value);
          }}
          data-testid="campaign-filter"
          sx={{ width: 140 }}
          InputProps={{
            endAdornment: campaignInput ? (
              <IconButton
                size="small"
                aria-label="Clear campaign filter"
                onClick={() => {
                  refilter();
                  setCampaignInput('');
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            ) : null,
          }}
        />

        <TextField
          size="small"
          type="number"
          label="IO ID"
          value={ioInput}
          onChange={(e) => {
            setIoInput(e.target.value);
          }}
          data-testid="io-filter"
          sx={{ width: 140 }}
          InputProps={{
            endAdornment: ioInput ? (
              <IconButton
                size="small"
                aria-label="Clear IO filter"
                onClick={() => {
                  refilter();
                  setIoInput('');
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            ) : null,
          }}
        />

        {isFetching && (
          <Typography variant="caption" color="text.secondary">
            Refreshing…
          </Typography>
        )}
      </Stack>

      {isError && (
        <Alert severity="error" data-testid="board-error">
          Could not load alerts{error instanceof Error ? `: ${error.message}` : ''}.
        </Alert>
      )}

      {isLoading && <Typography color="text.secondary">Loading alerts…</Typography>}

      {!isLoading && !isError && data && data.contracts.length === 0 && (
        <Box data-testid="empty-state">
          <Typography color="text.secondary">
            {/* eslint-disable-next-line no-nested-ternary */}
            {narrowingFilters > 0
              ? 'No alerts match these filters.'
              : scope.kind === 'mine'
                ? 'No alerts are assigned to you.'
                : 'No alerts in this view.'}
          </Typography>
          {activeFilters.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" mt={1}>
              {activeFilters.map((f) => (
                <Chip
                  key={f.key}
                  size="small"
                  variant="outlined"
                  label={f.label}
                  onDelete={f.clear}
                  data-testid={`empty-filter-${f.key}`}
                />
              ))}
              <Button size="small" onClick={clearAllFilters} data-testid="clear-all-filters">
                Clear all filters
              </Button>
            </Stack>
          )}
          {scope.kind === 'mine' && (
            // The ONLY way the board leaves My-scope: an explicit click. Nothing
            // substitutes All on the user's behalf.
            <Button
              size="small"
              variant="contained"
              sx={{ mt: 1.5 }}
              data-testid="view-all-alerts"
              onClick={() => {
                refilter();
                setScope(ALL_SCOPE);
              }}
            >
              View all alerts
            </Button>
          )}
        </Box>
      )}

      {wide && selected ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: INSPECTOR_GRID, gap: 2, alignItems: 'start' }}>
          <Box sx={{ minWidth: 0 }}>
            {ledger}
            {pagination}
          </Box>
          <Paper
            variant="outlined"
            sx={{
              position: 'sticky',
              top: stickyTop + 8,
              borderRadius: 1.5,
              overflow: 'hidden',
              maxHeight: `calc(100vh - ${stickyTop + 32}px)`,
              display: 'flex',
            }}
          >
            {inspector}
          </Paper>
        </Box>
      ) : (
        <>
          {ledger}
          {pagination}
          <Drawer
            anchor="right"
            open={Boolean(selected) && !wide}
            onClose={() => setSelectedId(null)}
            PaperProps={{ sx: { width: 440, maxWidth: '100vw' } }}
          >
            {inspector}
          </Drawer>
        </>
      )}
    </Box>
  );
}

export default AlertsPage;
